import React from 'react'
import clsx from 'clsx'
import moment from 'moment'
import {KTSVG} from '../../../_metronic/helpers'
import {AdminNotification} from '../../types/admin-notification.type'
import {
  NOTIFICATION_TYPE_LABELS,
  getCountSummary,
  getNotificationDetail,
  getNotificationLink,
  getSeverityColor,
  getSeverityIconPath,
} from './notification-links'

type Props = {
  notification: AdminNotification
  onSelect: (notification: AdminNotification) => void
  compact?: boolean
}

const NotificationItem: React.FC<Props> = ({notification, onSelect, compact}) => {
  const color = getSeverityColor(notification.severity)
  const countSummary = getCountSummary(notification)
  const detail = getNotificationDetail(notification)
  const hasLink = Boolean(getNotificationLink(notification))

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={() => onSelect(notification)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect(notification)
        }
      }}
      className={clsx(
        'd-flex align-items-start border-bottom border-gray-200 cursor-pointer',
        compact ? 'px-5 py-4' : 'px-6 py-5',
        !notification.isRead && 'bg-light-primary',
        notification.severity === 'critical' && !notification.isRead && 'bg-light-danger'
      )}
    >
      <div className='symbol symbol-35px me-4 flex-shrink-0'>
        <span className={clsx('symbol-label', `bg-light-${color}`)}>
          <KTSVG
            path={getSeverityIconPath(notification.severity)}
            className={clsx('svg-icon-2', `svg-icon-${color}`)}
          />
        </span>
      </div>

      <div className='flex-grow-1 min-w-0'>
        <div className='d-flex align-items-center flex-wrap gap-2 mb-1'>
          <span
            className={clsx(
              'text-gray-800 text-hover-primary',
              notification.isRead ? 'fw-semibold' : 'fw-bold'
            )}
          >
            {notification.title}
          </span>
          {notification.count > 1 && (
            <span className={clsx('badge badge-sm', `badge-light-${color}`)}>
              {notification.count}
            </span>
          )}
          {!notification.isRead && (
            <span className='bullet bullet-dot bg-primary h-6px w-6px flex-shrink-0' />
          )}
        </div>

        {countSummary && <div className='text-gray-700 fs-7 mb-1'>{countSummary}</div>}

        {notification.body && (
          <div className='text-gray-600 fs-7 mb-1 text-truncate'>{notification.body}</div>
        )}

        {detail && <div className='text-gray-500 fs-8 mb-1'>{detail}</div>}

        <div className='d-flex align-items-center flex-wrap gap-2'>
          <span className='badge badge-light fs-8'>
            {NOTIFICATION_TYPE_LABELS[notification.type] ?? notification.type}
          </span>
          <span className='text-gray-400 fs-8'>{moment(notification.lastEventAt).fromNow()}</span>
          {!hasLink && <span className='text-gray-400 fs-8'>No linked screen</span>}
        </div>
      </div>
    </div>
  )
}

export {NotificationItem}
